import type {
  DataAccessAdapter,
  HostBindings,
  NavigationConnector,
  ResolvedPageContext,
} from './contracts'
import type { AiChatBotTool, EntityRegistry } from './types'

export type HostRuntime = {
  data: DataAccessAdapter
  navigation: NavigationConnector
  bindings: HostBindings
  /**
   * Per-host entity registry (aliases, relations, write policy). Injected by the
   * connector at bootstrap so the generic core tools stay entity-vocabulary-free;
   * read via getEntityRegistry() in engine/entity-registry.ts.
   */
  registry: EntityRegistry
  /** Resolves the current page from the product's URL scheme (permalinks → entity
   *  class, detail-page slugs → id). Returns null when the URL matches no known
   *  page, so the core falls back to dashboard. Injected by the connector so the
   *  core never hardcodes product URL vocabulary. */
  resolvePageFromUrl?: () => ResolvedPageContext | null
  /** Extracts the tab-appropriate start/end dates from a page's URL query.
   *  Product-specific (tab names differ per app); consumed by show_options'
   *  dateRange variant. */
  getUrlDateRange?: (urlQuery: Record<string, string> | undefined) => {
    start: string | undefined
    end: string | undefined
  }
}

let _runtime: HostRuntime | null = null

export function setHostRuntime(runtime: HostRuntime): void {
  _runtime = runtime
}

export function getHostRuntime(): HostRuntime {
  if (!_runtime) {
    throw new Error(
      'AiChatBot: getHostRuntime() called before setHostRuntime(). ' +
        'Call setHostRuntime() once during app bootstrap ' +
        '(useAiChatBotEnabled) before the bot is used.',
    )
  }
  return _runtime
}

// ─── Host Tool Pack ───────────────────────────────────────────────────────────

/**
 * Per-host product tools and prompt fragments. Injected by the connector at
 * bootstrap so the generic core (engine/prompts.ts, tools/index.ts) stays free
 * of CRM entity vocabulary. Read via getHostToolPack() at build-config time.
 */
export type HostToolPack = {
  /** CRM aggregation tools (team utilisation, orders portfolio, etc.) */
  aggregationTools: AiChatBotTool<unknown>[]
  /** Filter-builder tools; included in the tool list only when filterEnabled. */
  filterTools: AiChatBotTool<unknown>[]
  promptFragments: {
    /** Rendering rules for Activities, Tickets, Messages, Persons, and Deal pipeline;
     *  injected by the connector so the shared core stays CRM-vocabulary-free. */
    renderingRules?: string
    /** Time-tracking summary and utilisation instructions; injected by the connector.
     *  Included in the prompt only when the timeTracking feature flag is enabled. */
    timeTrackingRules?: string
    /** Segment-creation / refinement instructions; undefined when the List
     *  entity is not enabled on this instance so the core never references it. */
    segmentInstructions?: string
  }
  /** Display-name overrides for entity registry keys, e.g. { List: 'Segment' }. */
  entityDisplayNames?: Record<string, string>
}

let _toolPack: HostToolPack | null = null

export function setHostToolPack(pack: HostToolPack): void {
  _toolPack = pack
}

export function getHostToolPack(): HostToolPack {
  if (!_toolPack) {
    throw new Error(
      'AiChatBot: getHostToolPack() called before setHostToolPack(). ' +
        'Call setHostToolPack() once during app bootstrap ' +
        '(useAiChatBotEnabled) before the bot is used.',
    )
  }
  return _toolPack
}
